/**
 * Автоответчик: в нерабочее время на вопрос клиента в системной колонке, оставшийся без ответа,
 * подключённый Telegram-аккаунт отвечает шаблоном. Карточка при этом остаётся открытой.
 *
 * Нерабочее время: в будни всё вне «с … до …», в субботу и воскресенье — весь день или вне своих часов.
 * Время — в часовом поясе из общих настроек.
 *
 * Фразы — по периодам (по времени вопроса клиента): будни утро и вечер, суббота утро и вечер, воскресенье.
 * Утро — до начала рабочего дня (в том числе ночь после полуночи), вечер — после конца. Если у периода своих фраз
 * нет, берутся общие.
 */
import { z } from 'zod';

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Время в формате ЧЧ:ММ');

const weekendDaySchema = z.object({
  /** off — весь день нерабочий; hours — рабочий в указанные часы. */
  mode: z.enum(['off', 'hours']),
  from: hhmm,
  to: hhmm,
});

/**
 * Своё рабочее время для колонок (например, учёт с 10:00, а трейдеры с 08:30). Колонки из расписания считают
 * нерабочее время по нему: отметка «нерабочее», начало отсчёта ожидания, статистика. Остальные — по общему.
 */
export const columnScheduleSchema = z.object({
  id: z.string().trim().min(1).max(40),
  title: z.string().trim().min(1, 'Название расписания').max(60),
  columnKeys: z.array(z.string().trim().min(1)).default([]),
  weekdays: z.object({ from: hhmm, to: hhmm }),
  saturday: weekendDaySchema,
  sunday: weekendDaySchema,
});
export type ColumnSchedule = z.infer<typeof columnScheduleSchema>;
/** Расписание рабочего времени: общее (из настроек автоответчика) или своё у колонки. */
export type WorkSchedule = Pick<ColumnSchedule, 'weekdays' | 'saturday' | 'sunday'>;

/** Быстрый выбор задержки; можно задать и свою — от 1 минуты до суток. */
export const AUTOREPLY_DELAYS_SEC = [60, 120, 300, 600, 900, 1800, 3600] as const;
export const AUTOREPLY_MAX_TEXTS = 50;

const replyText = z.string().trim().min(1, 'Пустая фраза').max(4000);
const phrases = z.array(replyText).max(AUTOREPLY_MAX_TEXTS, `Не больше ${AUTOREPLY_MAX_TEXTS} фраз`);

/** Периоды нерабочего времени со своими словарями. */
export const AUTOREPLY_SLOTS = [
  { key: 'weekdayMorning', label: 'Будни, утро' },
  { key: 'weekdayEvening', label: 'Будни, вечер' },
  { key: 'saturdayMorning', label: 'Суббота, утро' },
  { key: 'saturdayEvening', label: 'Суббота, вечер' },
  { key: 'sunday', label: 'Воскресенье' },
] as const;
export type AutoreplySlot = (typeof AUTOREPLY_SLOTS)[number]['key'];

const autoreplyObject = z.object({
  enabled: z.boolean(),
  /** Рабочее время в будни (пн–пт). */
  weekdays: z.object({ from: hhmm, to: hhmm }),
  saturday: weekendDaySchema,
  sunday: weekendDaySchema,
  /** Сколько вопрос висит без ответа, прежде чем ответит автоответчик. */
  delaySec: z.number().int().min(60).max(24 * 3600),
  /**
   * Общие фразы — для периодов без своих фраз. Каждый раз отправляется случайная
   * (не та же, что в прошлый раз в этом чате).
   */
  texts: phrases,
  /** Свои фразы периодов; пустой список — берутся общие. */
  dictionaries: z
    .object({
      weekdayMorning: phrases.default([]),
      weekdayEvening: phrases.default([]),
      saturdayMorning: phrases.default([]),
      saturdayEvening: phrases.default([]),
      sunday: phrases.default([]),
    })
    .default({}),
  /** Отвечать реплаем на сообщение клиента. */
  replyToQuestion: z.boolean(),
  /**
   * Колонка «Пропущенные» (без своих правил): показывает все открытые карточки, созданные в нерабочее время,
   * из всех колонок, которые видит пользователь. null — не используется.
   */
  missedColumnKey: z.string().nullable().default(null),
  /**
   * Карточки из нерабочего времени: false — без таймера (висят с луной, пока не разберут);
   * true — время ожидания считается с начала ближайшего рабочего времени.
   */
  offHoursTimerFromWorkStart: z.boolean().default(false),
  /** Не отвечать на сообщения ботов (Telegram помечает как бота или username оканчивается на «bot»). */
  skipBots: z.boolean().default(true),
  /** Своё рабочее время для отдельных колонок. */
  columnSchedules: z.array(columnScheduleSchema).max(20).default([]),
});

/** Каждому периоду должно быть что отправить: свои фразы или общие. Колонка — не больше чем в одном расписании. */
const autoreplyChecked = autoreplyObject.superRefine((v, ctx) => {
  const seen = new Map<string, string>();
  v.columnSchedules.forEach((sch, i) => {
    for (const k of sch.columnKeys) {
      const other = seen.get(k);
      if (other) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['columnSchedules', i, 'columnKeys'], message: `Колонка уже есть в расписании «${other}»` });
      }
      seen.set(k, sch.title);
    }
  });
  if (v.texts.length) return;
  const empty = AUTOREPLY_SLOTS.filter((sl) => !v.dictionaries[sl.key].length);
  if (empty.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['texts'],
      message: `Добавьте общие фразы — у периодов без своих фраз нечего отправить: ${empty.map((sl) => sl.label.toLowerCase()).join(', ')}`,
    });
  }
});

/** Сохранённые до появления нескольких фраз настройки хранят один `text` — он становится первой фразой. */
export const autoreplySchema = z.preprocess((v) => {
  if (!v || typeof v !== 'object' || !('text' in v)) return v;
  const { text, ...rest } = v as Record<string, unknown>;
  return typeof text === 'string' && text.trim() ? { ...rest, texts: [text] } : rest;
}, autoreplyChecked);
export type AutoreplySettings = z.infer<typeof autoreplyObject>;
export type AutoreplyDictionaries = AutoreplySettings['dictionaries'];

/** Случайная фраза; если фраз несколько — не повторяет предыдущую. */
export function pickAutoreplyText(texts: readonly string[], previous: string | null = null, random: () => number = Math.random): string {
  const pool = texts.length > 1 && previous != null ? texts.filter((t) => t !== previous) : texts;
  const list = pool.length ? pool : texts;
  return list[Math.min(list.length - 1, Math.floor(random() * list.length))]!;
}

/**
 * Период нерабочего времени по времени вопроса. Утро — до начала рабочего дня, вечер — после конца;
 * если рабочих часов в этот день нет или они через полночь — делим по полудню.
 */
export function autoreplySlot(date: Date, s: AutoreplySettings, timezone: string): AutoreplySlot {
  const { weekday, minute } = localClock(date, timezone);
  if (weekday === 0) return 'sunday';
  const hours = weekday === 6 ? (s.saturday.mode === 'hours' ? s.saturday : null) : s.weekdays;
  const morning = hours && minutes(hours.from) < minutes(hours.to) ? minute < minutes(hours.from) : minute < 12 * 60;
  if (weekday === 6) return morning ? 'saturdayMorning' : 'saturdayEvening';
  return morning ? 'weekdayMorning' : 'weekdayEvening';
}

/** Фразы для периода: свои, а если их нет — общие. */
export function autoreplyTexts(s: AutoreplySettings, slot: AutoreplySlot): string[] {
  const own = s.dictionaries[slot];
  return own.length ? own : s.texts;
}

function minutes(t: string) {
  return Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5));
}
/** День недели (0 — вс) и минуты от полуночи в нужном часовом поясе. */
export function localClock(date: Date, timezone: string): { weekday: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .formatToParts(date)
    .reduce<Record<string, string>>((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(parts.weekday ?? 'Mon');
  return { weekday, minute: Number(parts.hour) * 60 + Number(parts.minute) };
}

/** Рабочие часы «с–до»; если «до» раньше «с» — интервал через полночь. */
function within(minute: number, from: string, to: string): boolean {
  const f = minutes(from);
  const t = minutes(to);
  if (f === t) return true;
  return f < t ? minute >= f && minute < t : minute >= f || minute < t;
}

export function isOffHours(date: Date, s: WorkSchedule, timezone: string): boolean {
  const { weekday, minute } = localClock(date, timezone);
  if (weekday === 6 || weekday === 0) {
    const day = weekday === 6 ? s.saturday : s.sunday;
    return day.mode === 'off' || !within(minute, day.from, day.to);
  }
  return !within(minute, s.weekdays.from, s.weekdays.to);
}

/**
 * Учитывать ли нерабочее время для карточек (отметка, «Пропущенные», таймер, статистика).
 * Работает при включённом автоответчике или выбранной колонке «Пропущенные».
 */
export function offHoursTracking(s: AutoreplySettings): boolean {
  return s.enabled || !!s.missedColumnKey;
}

/** Своё расписание колонки или null — колонка работает по общему времени. */
export function columnSchedule(s: AutoreplySettings, columnKey: string): ColumnSchedule | null {
  return s.columnSchedules.find((x) => x.columnKeys.includes(columnKey)) ?? null;
}

/**
 * Нерабочее время для карточки колонки: отметка и начало рабочего времени, с которого считается ожидание.
 * Колонка со своим расписанием учитывает его всегда; остальные — по общему, если учёт нерабочего времени включён.
 */
export function offHoursForColumn(
  s: AutoreplySettings,
  columnKey: string,
  at: Date,
  timezone: string,
): { offHours: boolean; workStartAt: Date | null } {
  const own = columnSchedule(s, columnKey);
  if (!own && !offHoursTracking(s)) return { offHours: false, workStartAt: null };
  const sched: WorkSchedule = own ?? s;
  if (!isOffHours(at, sched, timezone)) return { offHours: false, workStartAt: null };
  return { offHours: true, workStartAt: nextWorkStart(at, sched, timezone) };
}

/** Начало ближайшего рабочего времени после date (с точностью до минуты; не дальше 8 суток). */
export function nextWorkStart(date: Date, s: WorkSchedule, timezone: string): Date {
  if (!isOffHours(date, s, timezone)) return date;
  const step = 60_000;
  let t = Math.ceil(date.getTime() / step) * step;
  for (let i = 0; i < 8 * 24 * 60; i++) {
    if (!isOffHours(new Date(t), s, timezone)) return new Date(t);
    t += step;
  }
  return new Date(t);
}

/** Начало текущего нерабочего периода (с точностью до 5 минут); null — сейчас рабочее время. */
export function offPeriodStart(date: Date, s: WorkSchedule, timezone: string): Date | null {
  if (!isOffHours(date, s, timezone)) return null;
  const step = 5 * 60_000;
  let t = Math.floor(date.getTime() / step) * step;
  // выходные + праздничная ночь — не больше трёх суток назад
  for (let i = 0; i < (3 * 24 * 60) / 5; i++) {
    if (!isOffHours(new Date(t - step), s, timezone)) return new Date(t);
    t -= step;
  }
  return new Date(t);
}
